import { useState } from 'react';
import { dbSiteId, dbCountryId, dbPathwayId } from '../lib/ids';
import { insert } from '../lib/db';
import './capture.css';

/* =====================================================================================
   Field intelligence capture.

   The highest-value moment in the whole product is the ninety seconds after a congress
   conversation, and it usually happens on a phone with no signal. So this writes
   optimistically to the local overlay and queues for sync, never blocks on the network,
   and never loses input because the user was in a basement in Milan.

   Provenance is mandatory, not optional. Every record carries who said it, when, on what
   basis and how reliable it is, because intelligence without provenance decays into
   folklore within eighteen months and is then worse than having none.
   ===================================================================================== */

const CATEGORIES = [
  ['clinical_practice', 'Clinical practice'],
  ['procurement', 'Procurement'],
  ['budget', 'Budget'],
  ['incumbent', 'Incumbent supplier'],
  ['competitive', 'Competitive'],
  ['capacity', 'Capacity'],
  ['staffing', 'Staffing'],
  ['regulatory', 'Regulatory'],
  ['pricing', 'Pricing'],
  ['politics', 'Internal politics'],
  ['other', 'Other'],
] as const;

const SOURCES = [
  ['congress', 'Congress conversation'],
  ['call', 'Call'],
  ['meeting', 'Meeting'],
  ['site_visit', 'Site visit'],
  ['publication', 'Publication'],
  ['tender_document', 'Tender document'],
  ['distributor', 'Distributor'],
  ['press', 'Press'],
  ['inference', 'Inference'],
] as const;

interface Props {
  objectType: 'site' | 'country' | 'person' | 'competitor' | 'pathway';
  objectId: string;
  objectName: string;
  onClose: () => void;
}

export function CaptureIntelligence({ objectType, objectId, objectName, onClose }: Props) {
  const [headline, setHeadline] = useState('');
  const [detail, setDetail] = useState('');
  const [category, setCategory] = useState<string>('clinical_practice');
  const [sourceType, setSourceType] = useState<string>('congress');
  const [sourceDetail, setSourceDetail] = useState('');
  const [reliability, setReliability] = useState<'high' | 'medium' | 'low' | 'unconfirmed'>('medium');
  const [saved, setSaved] = useState(false);

  const canSave = headline.trim().length > 3 && sourceDetail.trim().length > 1;

  function save() {
    if (!canSave) return;
    const observed = new Date().toISOString().slice(0, 10);
    // 18-month freshness, matching the database default. Stale intelligence is actively
    // misleading, so it expires rather than quietly persisting.
    const expires = new Date(Date.now() + 548 * 86_400_000).toISOString().slice(0, 10);
    /* THE OBJECT ID IS A SEED SLUG AND THE COLUMN IS A uuid.

       Atlas renders from the bundled seed, where a centre is 'usa-nyu-langone'. atlas
       .intelligence.object_id is a uuid, so every captured observation was rejected with
       "invalid input syntax for type uuid" and sat unsynced in the browser queue. This is the
       same defect the site-scoped writes had, and the same deterministic conversion fixes it;
       the capture form was simply missed when the rest were corrected. */
    insert('intelligence', {
      objectType,
      objectId: objectType === 'site' ? dbSiteId(objectId)
              : objectType === 'country' ? dbCountryId(objectId)
              : objectType === 'pathway' ? dbPathwayId(objectId)
              : objectId,
      category,
      headline: headline.trim(),
      detail: detail.trim() || null,
      sourceType, sourceDetail: sourceDetail.trim(),
      observedOn: observed, expiresOn: expires,
      reliability,
      capturedOffline: !navigator.onLine,
    });
    setSaved(true);
    setTimeout(onClose, 900);
  }

  return (
    <>
      <div className="capture-scrim" onClick={onClose} />
      <div className="capture card" role="dialog" aria-label="Capture intelligence">
        <header className="capture-head">
          <div>
            <div className="label">Field intelligence</div>
            <div className="capture-object">{objectName}</div>
          </div>
          {!navigator.onLine && <span className="pill warn">Offline, will sync</span>}
        </header>

        {saved ? (
          <div className="capture-saved">
            <div className="capture-tick">✓</div>
            <div>
              <strong>Captured.</strong>
              <div className="drawer-note">
                {navigator.onLine ? 'Queued for sync.' : 'Held locally and queued. It will sync when you reconnect.'}
              </div>
            </div>
          </div>
        ) : (
          <div className="capture-body">
            <label className="capture-field">
              <span className="label">What did you learn</span>
              <input
                className="input" autoFocus value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="One line. They are unhappy with turnaround times."
              />
            </label>

            <label className="capture-field">
              <span className="label">Detail (optional)</span>
              <textarea
                className="input capture-textarea" rows={3} value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Anything worth remembering that the headline does not carry."
              />
            </label>

            <div className="capture-row">
              <label className="capture-field">
                <span className="label">Category</span>
                <select className="input" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <label className="capture-field">
                <span className="label">How you know</span>
                <select className="input" value={sourceType} onChange={(e) => setSourceType(e.target.value)}>
                  {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
            </div>

            <label className="capture-field">
              <span className="label">Source, specifically</span>
              <input
                className="input" value={sourceDetail}
                onChange={(e) => setSourceDetail(e.target.value)}
                placeholder="Who said it, and where. Required."
              />
              <span className="capture-hint">
                Required. An assessment with no attributable source cannot be checked later,
                and becomes folklore.
              </span>
            </label>

            <div className="capture-field">
              <span className="label">Reliability</span>
              <div className="seg capture-seg">
                {(['high', 'medium', 'low', 'unconfirmed'] as const).map((r) => (
                  <button key={r} className={reliability === r ? 'on' : ''} onClick={() => setReliability(r)}>
                    {r}
                  </button>
                ))}
              </div>
            </div>

            <footer className="capture-foot">
              <span className="capture-hint">Expires in 18 months unless reconfirmed.</span>
              <button className="btn" onClick={onClose}>Cancel</button>
              <button className="btn primary" disabled={!canSave} onClick={save}>Capture</button>
            </footer>
          </div>
        )}
      </div>
    </>
  );
}
